import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ClockIcon, CheckIcon, ShoppingCartIcon, GiftIcon, PlusCircleIcon, BellIcon, PenToolIcon, FileTextIcon, CalendarIcon, UsersIcon, AlertTriangleIcon, XIcon } from 'lucide-react';
import { useDarkMode } from '../../context/DarkModeContext';
import { SpiceLevelIndicator } from './SpiceLevelIndicator';
import { TagList } from './TagList';
import type { UpcomingBox } from './types';
interface UpcomingBoxesTabProps {
  relevantUpcomingBoxes: UpcomingBox[];
  trackedReleases: number[];
  toggleReleaseTracking: (releaseId: number) => void;
}
export const UpcomingBoxesTab: React.FC<UpcomingBoxesTabProps> = ({
  relevantUpcomingBoxes,
  trackedReleases,
  toggleReleaseTracking
}) => {
  const {
    darkMode
  } = useDarkMode();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const selectedBox = relevantUpcomingBoxes.find(b => b.id === selectedId) ?? null;
  const closeModal = useCallback(() => setSelectedId(null), []);
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeModal();
    };
    if (selectedBox) {
      document.addEventListener('keydown', handleKey);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [selectedBox, closeModal]);
  if (relevantUpcomingBoxes.length === 0) {
    return <div className={`p-12 rounded-2xl text-center border-2 border-dashed ${darkMode ? 'border-gray-700' : 'border-gray-200'}`} data-id="element-7724" data-mp="3gv1ngwth5qdv">
        <ClockIcon className={`h-12 w-12 mx-auto mb-3 opacity-40 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-7725" data-mp="3qn7fu7nn29tl" />
        <p className={darkMode ? 'text-gray-400' : 'text-gray-500'} data-id="element-7726" data-mp="2t7a273pyi30g">
          Upcoming box information will be revealed soon!
        </p>
      </div>;
  }
  return <>
      {/* Card Grid: 1 col mobile, 3 col desktop */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5" data-id="element-7727" data-mp="d36q0hpshf9">
        {relevantUpcomingBoxes.map(upcomingBox => {
        const isTracked = trackedReleases.includes(upcomingBox.id);
        return <button key={upcomingBox.id} onClick={() => setSelectedId(upcomingBox.id)} className="group relative rounded-2xl overflow-hidden text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 hover:-translate-y-0.5 transition-transform duration-200" data-id="element-7728" data-mp="a34ussa2hzu6">
              <div className="aspect-[4/3] relative" data-id="element-7729" data-mp="3rk5wqoltghnw">
                <img src={upcomingBox.image} alt={upcomingBox.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-7730" data-mp="3mk9sqpd4cq8c" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" data-id="element-7731" data-mp="11t0y0muo8vee" />

                {/* Theme badge top-left */}
                <div className="absolute top-3 left-3" data-id="element-7732" data-mp="1ecoh02kfgc3l">
                  <span className="bg-purple-600/90 backdrop-blur-sm text-white text-xs font-semibold px-2.5 py-1 rounded-full" data-id="element-7733" data-mp="1edg4eq3vf3is">
                    {upcomingBox.theme}
                  </span>
                </div>

                {/* Order-by date badge top-right */}
                <div className="absolute top-3 right-3" data-id="element-7734" data-mp="1t116qhsx8ypn">
                  <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-500/80 backdrop-blur-sm text-white flex items-center gap-1" data-id="element-7735" data-mp="1t3njcvuo64eq">
                    <AlertTriangleIcon className="w-3 h-3" data-id="element-7736" data-mp="1japzala147e0" />
                    Order by {upcomingBox.cutoffDate}
                  </span>
                </div>

                {/* Tracked badge */}
                {isTracked && <div className="absolute bottom-14 right-3" data-id="element-7737" data-mp="2bz4018si5ht9">
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-purple-600/90 backdrop-blur-sm text-white flex items-center gap-1" data-id="element-7738" data-mp="2grf1ig2w5bnx">
                      <CheckIcon className="w-3 h-3" data-id="element-7739" data-mp="s2zjluou47j0" />
                      Tracking
                    </span>
                  </div>}

                {/* Name bottom */}
                <div className="absolute bottom-0 left-0 right-0 p-4" data-id="element-7740" data-mp="j6qk6g0ux8wv">
                  <p className="text-white font-bold text-sm leading-tight" data-id="element-7741" data-mp="e6unl1uhojrc">
                    {upcomingBox.name}
                  </p>
                  <p className="text-white/60 text-xs mt-1 line-clamp-2" data-id="element-7742" data-mp="1bv4h0sg7lz6u">
                    {upcomingBox.description}
                  </p>
                </div>
              </div>
            </button>;
      })}
      </div>

      {/* Modal */}
      <AnimatePresence data-id="element-7743" data-mp="x3m5b6uduqta">
        {selectedBox && <>
            {/* Backdrop */}
            <motion.div key="backdrop" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.2
        }} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" onClick={closeModal} data-id="element-7744" data-mp="1ukuk02u4lr9u" />

            {/* Panel */}
            <motion.div key="panel" initial={{
          opacity: 0,
          y: 40,
          scale: 0.97
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 40,
          scale: 0.97
        }} transition={{
          duration: 0.25,
          ease: [0.32, 0.72, 0, 1]
        }} className={`fixed z-50 w-full sm:w-auto sm:max-w-2xl
                bottom-0 left-0 right-0
                sm:inset-0 sm:m-auto sm:h-fit
                rounded-t-2xl sm:rounded-2xl overflow-hidden shadow-2xl
                max-h-[90vh] flex flex-col
                ${darkMode ? 'bg-gray-900 text-gray-100' : 'bg-white text-gray-900'}`} data-id="element-7745" data-mp="2c1rqgsnpn9k8">
              {/* Modal Header */}
              <div className={`flex items-start justify-between px-6 py-4 border-b flex-shrink-0 ${darkMode ? 'border-gray-800' : 'border-gray-100'}`} data-id="element-7746" data-mp="1dhixeaaz3c4m">
                <div data-id="element-7747" data-mp="1nd442lqzl0kp">
                  <h3 className={`font-bold text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-7748" data-mp="1nckpnf0joubf">
                    {selectedBox.name}
                  </h3>
                  <p className={`text-sm ${darkMode ? 'text-purple-400' : 'text-purple-600'}`} data-id="element-7749" data-mp="1wczlh8b4xrt6">
                    {selectedBox.theme}
                  </p>
                </div>
                <button onClick={closeModal} className={`p-2 rounded-full transition-colors flex-shrink-0 ml-4 ${darkMode ? 'hover:bg-gray-800 text-gray-400 hover:text-gray-200' : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'}`} data-id="element-7750" data-mp="3u4v63xt0oy00">
                  <XIcon className="w-5 h-5" data-id="element-7751" data-mp="3o4csho05t7th" />
                </button>
              </div>

              {/* Scrollable Body */}
              <div className="overflow-y-auto flex-1 p-6 space-y-6" data-id="element-7752" data-mp="3f9olntexri2k">
                {/* Image */}
                <img src={selectedBox.image} alt={selectedBox.name} className="w-full rounded-xl object-cover aspect-video" data-id="element-7753" data-mp="30cfqeomihu5k" />

                {/* Cutoff warning — prominent */}
                <div className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium ${darkMode ? 'bg-amber-900/30 text-amber-300 border border-amber-800/50' : 'bg-amber-50 text-amber-700 border border-amber-200'}`} data-id="element-7754" data-mp="2re3ss2n89w3m">
                  <AlertTriangleIcon className="w-4 h-4 flex-shrink-0" data-id="element-7755" data-mp="uixxbgcv7hu9" />
                  Order by{' '}
                  <span className="font-bold mx-1" data-id="element-7756" data-mp="233d7gp6c8e7j">
                    {selectedBox.cutoffDate}
                  </span>{' '}
                  to receive this box
                </div>

                {/* Ships badge */}
                <div className="flex items-center gap-2" data-id="element-7757" data-mp="1tcsb78e4zlrs">
                  <CalendarIcon className={`w-4 h-4 ${darkMode ? 'text-purple-400' : 'text-purple-600'}`} data-id="element-7758" data-mp="1pdt9rxrm6753" />
                  <span className={`text-sm font-medium ${darkMode ? 'text-purple-400' : 'text-purple-600'}`} data-id="element-7759" data-mp="1kf89jycd3ngn">
                    Ships: {selectedBox.releaseDate}
                  </span>
                </div>

                {/* Description */}
                <p className={`leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-7760" data-mp="16fqg35o4fm0y">
                  {selectedBox.description}
                </p>

                {/* Featured Book */}
                {selectedBox.books && selectedBox.books.length > 0 && <div data-id="element-7761" data-mp="mk5mffwpcq3w">
                    <h4 className={`text-xs font-bold uppercase tracking-wider mb-4 flex items-center gap-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7762" data-mp="rjij0sddlbjo">
                      Featured Book
                    </h4>
                    <div className="space-y-4" data-id="element-7763" data-mp="10baqqvb0m6kk">
                      {selectedBox.books.map((book, i) => <div key={i} className={`p-4 rounded-xl border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-100'}`} data-id="element-7764" data-mp="3ne4pawt6j1y" data-mpi={i}>
                          <h5 className={`font-bold mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-7765" data-mp="hlb5ykm7jdmy">
                            {book.title}
                          </h5>
                          <p className={`text-sm mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7766" data-mp="1szoln26t4mrw">
                            by{' '}
                            <Link to={`/author/${book.authorId}`} className={`font-medium hover:underline ${darkMode ? 'text-purple-400' : 'text-purple-600'}`} data-id="element-7767" data-mp="37citsn7ajnjq">
                              {book.author}
                            </Link>
                            {book.releaseDate && <span className={`ml-2 text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-7768" data-mp="2oe6nraboetsd">
                                · Releases {book.releaseDate}
                              </span>}
                          </p>
                          <div className="mb-3 mt-3" data-id="element-7769" data-mp="8mio375a2n5v">
                            <SpiceLevelIndicator level={book.spiceLevel} data-id="element-7770" data-mp="covwy4fbn16v" />
                          </div>
                          {book.tropes && book.tropes.length > 0 && <div className="mb-2" data-id="element-7771" data-mp="1x90vywx8dt5s">
                              <span className={`text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7772" data-mp="1t6e26qh01y5y">
                                Tropes
                              </span>
                              <TagList items={book.tropes} type="trope" data-id="element-7773" data-mp="1egyg3nseygwl" />
                            </div>}
                          {book.contentWarnings && book.contentWarnings.length > 0 && <div className="mb-3" data-id="element-7774" data-mp="15huti9u1fjdc">
                                <span className={`text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7775" data-mp="10hfktvkpb01p">
                                  Content Warnings
                                </span>
                                <TagList items={book.contentWarnings} type="warning" data-id="element-7776" data-mp="3dy8tnltt2k2s" />
                              </div>}
                          <div className={`pt-3 mt-1 border-t grid grid-cols-1 sm:grid-cols-2 gap-2 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`} data-id="element-7777" data-mp="34wsi3ar98bwz">
                            <div className="flex items-center gap-1.5" data-id="element-7778" data-mp="5ji1p89bqyyh">
                              <PenToolIcon className={`h-3.5 w-3.5 flex-shrink-0 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-7779" data-mp="5ip1yr8s99pu" />
                              <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7780" data-mp="3smd4k3mm9rb0">
                                Cover:{' '}
                              </span>
                              <Link to={`/talent/designers/${book.coverDesignerId}`} className={`text-xs font-medium hover:underline ${darkMode ? 'text-purple-400' : 'text-purple-600'}`} data-id="element-7781" data-mp="29p0axvxbriu3">
                                {book.coverDesigner}
                              </Link>
                            </div>
                            <div className="flex items-center gap-1.5" data-id="element-7782" data-mp="1q0rayzpji8k1">
                              <FileTextIcon className={`h-3.5 w-3.5 flex-shrink-0 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-7783" data-mp="1lzxomarqx1t9" />
                              <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7784" data-mp="1v0m294d8905m">
                                Format:{' '}
                              </span>
                              <Link to={`/services/production/${book.formatterId}`} className={`text-xs font-medium hover:underline ${darkMode ? 'text-purple-400' : 'text-purple-600'}`} data-id="element-7785" data-mp="12b6q2b9tt184">
                                {book.formatter}
                              </Link>
                            </div>
                          </div>

                          {/* Add to tracker */}
                          <div className="mt-3" data-id="element-7786" data-mp="1yo4zkzx4ammo">
                            <button onClick={e => {
                      e.stopPropagation();
                      toggleReleaseTracking(selectedBox.id);
                    }} className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg transition-colors ${trackedReleases.includes(selectedBox.id) ? darkMode ? 'bg-purple-900/40 text-purple-300' : 'bg-purple-100 text-purple-700' : darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-7787" data-mp="1yrj2qadluzv6">
                              {trackedReleases.includes(selectedBox.id) ? <>
                                  <CheckIcon className="h-3.5 w-3.5" data-id="element-7788" data-mp="2npkug27s2yuz" />
                                  Added to Release Tracker
                                </> : <>
                                  <PlusCircleIcon className="h-3.5 w-3.5" data-id="element-7789" data-mp="3fh0gnv5fajko" />
                                  Add to Release Tracker
                                </>}
                            </button>
                          </div>
                        </div>)}
                    </div>
                  </div>}

                {/* Teaser Items */}
                <div data-id="element-7790" data-mp="1uzc8s0e19wbn">
                  <h4 className={`text-xs font-bold uppercase tracking-wider mb-3 flex items-center gap-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7791" data-mp="1q2vj31r6r9wg">
                    <GiftIcon className="w-4 h-4" data-id="element-7792" data-mp="1wim12nxku479" />
                    Teaser Items
                  </h4>
                  <ul className="space-y-2" data-id="element-7793" data-mp="1ijmbne28pla1">
                    {selectedBox.teaserItems.map((item, i) => <li key={i} className="flex items-center gap-2" data-id="element-7794" data-mp="1nhecwyoryjqw" data-mpi={i}>
                        <div className="w-1.5 h-1.5 rounded-full bg-purple-500 flex-shrink-0" data-id="element-7795" data-mp="18t9ulvtmwa8n" />
                        <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-7796" data-mp="1djzzl7y5eb2t">
                          {item}
                        </span>
                      </li>)}
                  </ul>
                </div>

                {/* Featured Artists */}
                {selectedBox.artists && selectedBox.artists.length > 0 && <div data-id="element-7797" data-mp="utryswhx7wps">
                    <h4 className={`text-xs font-bold uppercase tracking-wider mb-3 flex items-center gap-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7798" data-mp="us77rgutevwo">
                      <UsersIcon className="w-4 h-4" data-id="element-7799" data-mp="6a6q0mpy4tds" />
                      Featured Artists
                    </h4>
                    <div className="space-y-2" data-id="element-7800" data-mp="l3tgjlb7ml6e">
                      {selectedBox.artists.map((artist, i) => <div key={i} data-id="element-7801" data-mp="g9610by6p13y" data-mpi={i}>
                          <Link to={`/talent/artists/${artist.id}`} className={`text-sm font-medium hover:underline ${darkMode ? 'text-purple-400' : 'text-purple-600'}`} data-id="element-7802" data-mp="u5syqgxynvax">
                            {artist.name}
                          </Link>
                          <p className={`text-xs mt-0.5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-7803" data-mp="yxt5as8jdmgh">
                            {artist.items.join(', ')}
                          </p>
                        </div>)}
                    </div>
                  </div>}

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-3 pt-2 pb-2" data-id="element-7804" data-mp="2k7op9mjfkbh2">
                  <button className="bg-purple-600 hover:bg-purple-700 text-white font-semibold py-2.5 px-5 rounded-xl inline-flex items-center gap-2 transition-colors text-sm" data-id="element-7805" data-mp="2p8cmipy95ojq">
                    <ShoppingCartIcon className="h-4 w-4" data-id="element-7806" data-mp="1wk7100zdjviu" />
                    Pre-order This Box
                  </button>
                  <button onClick={() => toggleReleaseTracking(selectedBox.id)} className={`inline-flex items-center gap-2 font-semibold py-2.5 px-5 rounded-xl border transition-colors text-sm ${trackedReleases.includes(selectedBox.id) ? darkMode ? 'bg-purple-900/30 text-purple-300 border-purple-700' : 'bg-purple-50 text-purple-700 border-purple-300' : darkMode ? 'bg-transparent text-gray-300 border-gray-600 hover:bg-gray-800' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`} data-id="element-7807" data-mp="21k37c6vjnz1k">
                    {trackedReleases.includes(selectedBox.id) ? <>
                        <CheckIcon className="h-4 w-4" data-id="element-7808" data-mp="15am0go672ndf" />
                        Tracking
                      </> : <>
                        <BellIcon className="h-4 w-4" data-id="element-7809" data-mp="wb8pts6c3sr4" />
                        Track Release
                      </>}
                  </button>
                </div>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </>;
};